/**
 * Data layer for `email-notification-templates`. Thin typed fetch wrappers over /api/v1/email-notification-templates.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface EmailNotificationTemplatesRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListEmailNotificationTemplatesResponse {
	count: number;
	items: EmailNotificationTemplatesRow[];
}

export interface CreateEmailNotificationTemplatesBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/email-notification-templates";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListEmailNotificationTemplatesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListEmailNotificationTemplatesResponse>(res);
}

export async function create(body: CreateEmailNotificationTemplatesBody): Promise<EmailNotificationTemplatesRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<EmailNotificationTemplatesRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const emailNotificationTemplatesQueryKeys = {
	all: ["email-notification-templates"] as const,
	list: () => [...emailNotificationTemplatesQueryKeys.all] as const,
	detail: (id: string) => [...emailNotificationTemplatesQueryKeys.all, id] as const,
};
